import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import axios from 'axios';
import Botpoison from '@botpoison/browser';

// Initialize Botpoison with the public key
const botpoison = new Botpoison({
  publicKey: "pk_99f84e7d-fd26-420e-9e91-139319a8200e"
});

const ContactSection = styled.section`
  padding: 100px 0;
  background-color: ${theme.colors.light};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${theme.colors.taupe}, transparent);
    opacity: 0.3;
  }
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.heading};
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: 15px;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.caramel};
    margin: 15px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.3rem;
    
    &::after {
      width: 60px;
      height: 2px;
      margin: 12px auto 0;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 1.2rem;
  color: ${theme.colors.taupe};
  text-align: center;
  margin-bottom: 60px;
  font-style: italic;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 50px;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 40px;
  }
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 25px;

  @media (max-width: 1024px) {
    gap: 30px;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
  
  @media (max-width: 480px) {
    padding: 0 15px;
    gap: 40px;
  }
`;

const ContactCard = styled.div`
  background-color: white;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  padding: 40px;
  height: 100%;
  border: 1px solid rgba(142, 127, 125, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.large};
  }
  
  @media (max-width: 768px) {
    padding: 35px;
  }
  
  @media (max-width: 480px) {
    padding: 25px;
    border-radius: ${theme.borderRadius.small};
  }
`;

const CardTitle = styled.h3`
  font-family: ${theme.fonts.heading};
  font-size: 1.8rem;
  color: ${theme.colors.secondary};
  margin-bottom: 25px;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 2px;
    background-color: ${theme.colors.burgundy};
    margin-top: 10px;
    opacity: 0.7;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin-bottom: 20px;
    
    &::after {
      width: 35px;
      margin-top: 8px;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${theme.colors.dark};
  font-family: ${theme.fonts.accent};
  font-size: 0.9rem;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 14px;
  border: 1px solid rgba(142, 127, 125, 0.2);
  border-radius: ${theme.borderRadius.small};
  font-family: ${theme.fonts.body};
  font-size: 16px;
  transition: all 0.3s ease;
  background-color: rgba(245, 241, 237, 0.5);

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
    background-color: white;
  }
`;

const TextArea = styled.textarea`
  padding: 14px;
  border: 1px solid rgba(142, 127, 125, 0.2);
  border-radius: ${theme.borderRadius.small};
  font-family: ${theme.fonts.body};
  font-size: 16px;
  min-height: 140px;
  resize: vertical;
  transition: all 0.3s ease;
  background-color: rgba(245, 241, 237, 0.5);

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
    background-color: white;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const SubmitButton = styled.button`
  font-family: ${theme.fonts.accent};
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${theme.colors.light};
  background-color: ${theme.colors.burgundy};
  padding: 16px 32px;
  border: 2px solid ${theme.colors.burgundy};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 10px;
  box-shadow: ${theme.shadows.small};
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    margin-top: 5px;
  }
`;

const ReservationButton = styled.a`
  font-family: ${theme.fonts.accent};
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${theme.colors.light};
  background-color: ${theme.colors.caramel};
  padding: 16px 32px;
  border: 2px solid ${theme.colors.caramel};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 20px;
  box-shadow: ${theme.shadows.small};
  text-decoration: none;
  display: inline-block;
  text-align: center;
  
  &:hover {
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    margin-top: 15px;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 25px 0 35px;
  
  @media (max-width: 480px) {
    margin: 20px 0 30px;
  }
`;

const InfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-family: ${theme.fonts.body};
  font-size: 1.1rem;
  color: ${theme.colors.dark};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 15px;
  }
`;

const InfoIcon = styled.span`
  color: ${theme.colors.burgundy};
  font-size: 1.5rem;
  margin-right: 15px;
  min-width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-right: 12px;
    min-width: 20px;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-top: 20px;
  }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.colors.dark};
  color: ${theme.colors.light};
  font-size: 1.3rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.burgundy};
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 1.1rem;
  }
`;

const Map = styled.iframe`
  width: 100%;
  height: 250px;
  border: none;
  border-radius: ${theme.borderRadius.small};
  margin-top: 25px;
  box-shadow: ${theme.shadows.small};
  
  @media (max-width: 480px) {
    height: 200px;
    margin-top: 20px;
  }
`;

const SuccessMessage = styled.div`
  padding: 25px;
  background: linear-gradient(135deg, rgba(139, 69, 19, 0.1), rgba(218, 165, 32, 0.1));
  border-radius: ${theme.borderRadius.medium};
  margin-top: 20px;
  color: ${theme.colors.primary};
  text-align: center;
  font-family: ${theme.fonts.body};
  font-size: 1.1rem;
  font-weight: 500;
  border: 2px solid rgba(139, 69, 19, 0.2);
  box-shadow: ${theme.shadows.medium};
  position: relative;
  overflow: hidden;
  animation: slideIn 0.5s ease-out;
  
  &::before {
    content: '✓';
    position: absolute;
    top: -5px;
    left: 20px;
    font-size: 2rem;
    color: ${theme.colors.caramel};
    font-weight: bold;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    font-size: 1rem;
    margin-top: 15px;
    
    &::before {
      font-size: 1.5rem;
      left: 15px;
    }
  }
`;

const ContactReservation: React.FC = () => {
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    partySize: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccess(false); // Hide any existing success message

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      await axios.post('https://submit-form.com/YQsj9deEA', {
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        date: formValues.date,
        time: formValues.time,
        partySize: formValues.partySize,
        message: formValues.message,
        // Include botpoison solution
        _botpoison: solution,
      });

      // Show success message instead of alert
      setShowSuccess(true);
      setFormValues({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        partySize: '',
        message: '',
      });

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      alert('Sorry, there was an error submitting your request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <SectionTitle>Contact & Reservations</SectionTitle>
      <SectionSubtitle>
        We'd love to hear from you and welcome you to our tables
      </SectionSubtitle>
      <ContactContainer>
        <ContactCard>
          <CardTitle>Make a Reservation</CardTitle>
          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="date">Date</Label>
                <Input
                  type="date"
                  id="date"
                  name="date"
                  value={formValues.date}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label htmlFor="time">Time</Label>
                <Input
                  type="time"
                  id="time"
                  name="time"
                  value={formValues.time}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="partySize">Party Size</Label>
                <Input
                  type="number"
                  id="partySize"
                  name="partySize"
                  min="1"
                  max="20"
                  value={formValues.partySize}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Label htmlFor="message">Special Requests</Label>
              <TextArea
                id="message"
                name="message"
                value={formValues.message}
                onChange={handleChange}
                placeholder="Any special requests or dietary restrictions"
              />
            </FormGroup>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Request'}
            </SubmitButton>
            {showSuccess && (
              <SuccessMessage>
                🎉 Thank you for your reservation request!<br />
                We'll get back to you soon to confirm your booking.
              </SuccessMessage>
            )}
          </Form>
        </ContactCard>

        <ContactCard>
          <CardTitle>Find Us</CardTitle>
          <InfoList>
            <InfoItem>
              <InfoIcon>📍</InfoIcon>
              <span>614 Sutter St. | Suite C, Folsom, CA 95630</span>
            </InfoItem>
            <InfoItem>
              <InfoIcon>📞</InfoIcon>
              <span>(916) 693-6786</span>
            </InfoItem>
            <InfoItem>
              <InfoIcon>📧</InfoIcon>
              <span>info@fiorifolsom.com</span>
            </InfoItem>
            <InfoItem>
              <InfoIcon>🕒</InfoIcon>
              <span>
                Sunday - Thursday: 11am - 9pm<br />
                Friday - Saturday: 11am - 10pm<br />
              </span>
            </InfoItem>
          </InfoList>
          <ReservationButton
            href="https://www.yelp.com/biz/fiori-italian-pizzeria-folsom"
            target="_blank"
            rel="noopener noreferrer"
          >
            Make a Reservation
          </ReservationButton>
          <Map
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3117.1452986060233!2d-121.18288042450619!3d38.67376076601406!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ae4ac79b1f7df%3A0x6e35e23e5b7f54c5!2s614%20Sutter%20St%20c%2C%20Folsom%2C%20CA%2095630!5e0!3m2!1sen!2sus!4v1681349050079!5m2!1sen!2sus"
            width="100%"
            height="250"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></Map>
        </ContactCard>
      </ContactContainer>
    </ContactSection>
  );
};

export default ContactReservation; 